import { useState, useMemo } from 'react';
import { useAssets, useGoals, useResidencyCountries } from '../../hooks/useApi';
import { AddAssetModal } from '../../components/AddAssetModal/AddAssetModal';
import { AssetDetailModal } from '../../components/AssetDetailModal/AssetDetailModal';
import { AssetAccount, ACCOUNT_TYPE_LABELS } from '../../types/api.types';
import { formatAmount } from '../../utils/numberFormat';
import styles from './AssetsPage.module.scss';

const ACCOUNT_TYPE_COLORS: Record<string, string> = {
    BROKERAGE: 'primary',
    RETIREMENT_401K: 'success',
    ROTH_IRA: 'info',
    TRADITIONAL_IRA: 'warning',
    HSA: 'secondary',
    INDIA_PPF: 'tertiary',
    INDIA_NRI: 'primary',
    OTHER_ASSET: 'secondary',
};

export default function AssetsPage() {
    const { assets, loading, refetch, deleteAsset } = useAssets();
    const { goals } = useGoals();
    const { countryOptions, countries } = useResidencyCountries();

    const defaultCountry = countries[0] ?? 'INDIA';
    const [showAddAsset, setShowAddAsset] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<AssetAccount | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredAssets = useMemo(() => {
        return assets.filter(
            (a) =>
                a.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.country.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [assets, searchTerm]);

    const totalBalance = filteredAssets.reduce((sum, a) => sum + a.balance, 0);

    if (loading) {
        return (
            <div className={styles.assetsPage}>
                <p className={styles.loading}>Loading your assets...</p>
            </div>
        );
    }

    return (
        <div className={styles.assetsPage}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>All Assets</h1>
                    <p className={styles.subtitle}>Manage and track all your investment accounts across countries</p>
                </div>
                <button className={styles.addButton} onClick={() => setShowAddAsset(true)}>
                    + Add Asset
                </button>
            </div>

            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <span className={styles.statLabel}>Total Balance</span>
                    <span className={styles.statValue}>{formatAmount(totalBalance, 'USD')}</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statLabel}>Accounts</span>
                    <span className={styles.statValue}>{filteredAssets.length}</span>
                </div>
            </div>

            <div className={styles.searchBar}>
                <input
                    type="text"
                    placeholder="Search by label or country..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />
            </div>

            {filteredAssets.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>
                        No assets found.{' '}
                        {searchTerm ? 'Try adjusting your search.' : 'Create your first account to get started.'}
                    </p>
                    {!searchTerm && (
                        <button className={styles.addButton} onClick={() => setShowAddAsset(true)}>
                            + Add your first asset
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.assetGrid}>
                    {filteredAssets.map((asset: AssetAccount) => (
                        <div key={asset.accountId} className={styles.assetCard} onClick={() => setSelectedAsset(asset)}>
                            <div className={styles.cardHeader}>
                                <div className={styles.cardTitle}>
                                    <h3 className={styles.assetName}>{asset.label}</h3>
                                    <span className={styles.assetCountry}>{asset.country}</span>
                                </div>
                                <span
                                    className={`${styles.badge} ${styles[`badge-${ACCOUNT_TYPE_COLORS[asset.accountType] || 'secondary'}`]}`}
                                >
                                    {ACCOUNT_TYPE_LABELS[asset.accountType]}
                                </span>
                            </div>

                            <div className={styles.cardContent}>
                                <div className={styles.metaRow}>
                                    <span className={styles.metaLabel}>Balance</span>
                                    <span className={styles.balance}>
                                        {formatAmount(asset.balance, asset.currency)}
                                    </span>
                                </div>
                                <div className={styles.metaRow}>
                                    <span className={styles.metaLabel}>Expected Return</span>
                                    <span className={styles.returnValue}>{asset.expectedReturn}%</span>
                                </div>
                                <div className={styles.metaRow}>
                                    <span className={styles.metaLabel}>Ownership</span>
                                    <span className={styles.ownershipTag}>{asset.ownership}</span>
                                </div>
                            </div>

                            <div className={styles.cardActions}>
                                <button
                                    className={styles.editButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedAsset(asset);
                                    }}
                                >
                                    View/Edit
                                </button>
                                <button
                                    className={styles.deleteButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (confirm(`Delete "${asset.label}"? This cannot be undone.`)) {
                                            deleteAsset(asset.accountId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAddAsset && (
                <AddAssetModal
                    defaultCountry={defaultCountry}
                    countryOptions={countryOptions}
                    onClose={() => setShowAddAsset(false)}
                    onAdded={() => {
                        setShowAddAsset(false);
                        refetch();
                    }}
                />
            )}
            {selectedAsset && (
                <AssetDetailModal
                    asset={selectedAsset}
                    goals={goals}
                    onClose={() => setSelectedAsset(null)}
                    onSaved={() => {
                        setSelectedAsset(null);
                        refetch();
                    }}
                />
            )}
        </div>
    );
}
